// src/components/remediation/ValidationRunModal.jsx
import React from "react";
import {
  getTaskStatusLabel,
} from "../../utils/complianceFormatters";

function ValidationMetricCard({ label, value, tone = "default" }) {
  const toneStyle =
    tone === "danger"
      ? { color: "var(--danger)" }
      : tone === "warn"
      ? { color: "var(--warn)" }
      : tone === "ok"
      ? { color: "var(--ok)" }
      : {};

  return (
    <div className="hero-kpi">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value small" style={toneStyle}>
        {value}
      </div>
    </div>
  );
}

function ValidationMetaField({ label, value, wrap = false }) {
  return (
    <div className="open-item-pill" style={{ cursor: "default", alignItems: "flex-start" }}>
      <div className="open-item-label">{label}</div>
      <div
        className="open-item-value"
        style={{
          fontSize: 12,
          maxWidth: "100%",
          textAlign: "right",
          whiteSpace: wrap ? "normal" : "nowrap",
          overflow: wrap ? "visible" : "hidden",
          textOverflow: wrap ? "clip" : "ellipsis",
          wordBreak: wrap ? "break-word" : "normal",
          lineHeight: 1.35,
        }}
        title={String(value || "—")}
      >
        {value || "—"}
      </div>
    </div>
  );
}

export default function ValidationRunModal({
  validationRunModal,
  setValidationRunModal,
  fullAssessmentRunning,
  remediationTaskUpdating,
  activeRemediationTasks = [],
  createdRemediationTask,
  handleUpdateRemediationTask,
  handleOpenFixPlan,
  fixPlanItem,
  setMode,
  framework,
  lastSyncJobId,
  handleRunFullCloudAssessment,
}) {
  if (!validationRunModal?.open) return null;

  const toneColor =
    validationRunModal.tone === "ok"
      ? "var(--ok)"
      : validationRunModal.tone === "danger"
      ? "var(--danger)"
      : validationRunModal.tone === "warn"
      ? "var(--warn)"
      : "var(--accent)";

  const toneBorderColor =
    validationRunModal.tone === "ok"
      ? "color-mix(in srgb, var(--ok), transparent 70%)"
      : validationRunModal.tone === "danger"
      ? "color-mix(in srgb, var(--danger), transparent 70%)"
      : validationRunModal.tone === "warn"
      ? "color-mix(in srgb, var(--warn), transparent 70%)"
      : "color-mix(in srgb, var(--accent), transparent 70%)";

  const taskForModal =
    activeRemediationTasks.find((task) => task.id === validationRunModal.taskId) ||
    createdRemediationTask;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Validation result"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 120,
        background: "rgba(2, 6, 23, 0.78)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "72px 24px 24px",
        overflowY: "auto",
      }}
      onClick={() => {
        if (!fullAssessmentRunning) setValidationRunModal(null);
      }}
    >
      <section
        className="card"
        style={{
          width: "min(720px, calc(100vw - 48px))",
          maxHeight: "calc(100vh - 104px)",
          overflow: "auto",
          boxShadow: "0 24px 80px rgba(0,0,0,0.48)",
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="card-header">
          <div>
            <div className="dash-eyebrow">OASYS · Remediation Validation</div>
            <div className="card-title">{validationRunModal.title}</div>
            <div
              style={{
                color: "var(--muted)",
                fontSize: 13,
                lineHeight: 1.45,
                marginTop: 4,
              }}
            >
              {validationRunModal.subtitle}
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <span
              className="task-severity"
              style={{
                color: toneColor,
                borderColor: toneBorderColor,
              }}
            >
              {validationRunModal.phase === "running"
                ? "Running"
                : getTaskStatusLabel(validationRunModal.phase)}
            </span>

            {!fullAssessmentRunning ? (
              <button
                className="btn btn-ghost btn-xs"
                type="button"
                onClick={() => setValidationRunModal(null)}
              >
                Close
              </button>
            ) : null}
          </div>
        </div>

        <div className="card-body" style={{ display: "grid", gap: 14 }}>
          <div
            style={{
              border: "1px solid color-mix(in srgb, var(--text), transparent 86%)",
              borderRadius: 16,
              padding: 14,
              display: "grid",
              gap: 10,
            }}
          >
            <div
              style={{
                fontSize: 18,
                fontWeight: 900,
                color:
                  validationRunModal.tone === "ok"
                    ? "var(--ok)"
                    : validationRunModal.tone === "danger"
                    ? "var(--danger)"
                    : validationRunModal.tone === "warn"
                    ? "var(--warn)"
                    : "var(--text)",
              }}
            >
              {validationRunModal.summary}
            </div>

            <div style={{ color: "var(--text)", fontSize: 13, lineHeight: 1.55 }}>
              {validationRunModal.detail}
            </div>

            {validationRunModal.stats ? (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                  gap: 8,
                }}
              >
                <ValidationMetricCard label="Checked" value={validationRunModal.stats.checked} />
                <ValidationMetricCard label="Passed" value={validationRunModal.stats.passed} tone="ok" />
                <ValidationMetricCard label="Failed" value={validationRunModal.stats.failed} tone="danger" />
                <ValidationMetricCard label="Skipped" value={validationRunModal.stats.skipped} tone="warn" />
              </div>
            ) : null}
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 8,
            }}
          >
            <ValidationMetaField label="Task ID" value={validationRunModal.taskId || "—"} wrap />
            <ValidationMetaField label="Rule" value={validationRunModal.ruleId || "—"} wrap />
            <ValidationMetaField label="Sync Job" value={validationRunModal.syncJobId || lastSyncJobId || "—"} wrap />
            <ValidationMetaField label="Framework" value={framework} />
          </div>

          {Array.isArray(validationRunModal.nextSteps) && validationRunModal.nextSteps.length > 0 ? (
            <div
              style={{
                border: "1px solid color-mix(in srgb, var(--text), transparent 86%)",
                borderRadius: 16,
                padding: 14,
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 900,
                  textTransform: "uppercase",
                  letterSpacing: ".06em",
                  color: "var(--muted)",
                  marginBottom: 10,
                }}
              >
                Next steps
              </div>

              <ol style={{ margin: 0, paddingLeft: 20, display: "grid", gap: 8 }}>
                {validationRunModal.nextSteps.map((step, index) => (
                  <li key={`${step}-${index}`} style={{ fontSize: 13, lineHeight: 1.55 }}>
                    {step}
                  </li>
                ))}
              </ol>
            </div>
          ) : null}

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
            {validationRunModal.tone === "ok" && validationRunModal.taskId ? (
              <button
                className="btn btn-ghost btn-xs"
                type="button"
                disabled={fullAssessmentRunning || remediationTaskUpdating}
                onClick={async () => {
                  const taskToClose = taskForModal;

                  if (!taskToClose?.id) return;

                  await handleUpdateRemediationTask(
                    taskToClose,
                    {
                      status: "closed",
                      closure_reason: "Closed after OASYS validation passed.",
                    },
                    "Validated task closed."
                  );

                  setValidationRunModal(null);
                }}
              >
                Close Task
              </button>
            ) : null}

            {validationRunModal.taskId ? (
              <button
                className="btn btn-ghost btn-xs"
                type="button"
                disabled={fullAssessmentRunning}
                onClick={() => {
                  const taskToOpen = taskForModal;

                  setValidationRunModal(null);

                  if (taskToOpen?.id) {
                    handleOpenFixPlan(
                      {
                        ...taskToOpen,
                        framework: taskToOpen.framework || framework,
                        control_id: taskToOpen.control_id || validationRunModal.controlId,
                        rule_id: taskToOpen.rule_id || validationRunModal.ruleId,
                        resource_id: taskToOpen.resource_id || validationRunModal.resourceId,
                        reason_summary: taskToOpen.description,
                        state: taskToOpen.status,
                      },
                      taskToOpen.source || "task"
                    );
                  }
                }}
              >
                Open Task
              </button>
            ) : null}

            {validationRunModal.tone === "danger" && fixPlanItem ? (
              <button
                className="btn btn-ghost btn-xs"
                type="button"
                disabled={fullAssessmentRunning}
                onClick={() => {
                  setValidationRunModal(null);
                  handleOpenFixPlan(fixPlanItem, fixPlanItem.source || "risk");
                }}
              >
                Open Fix Plan
              </button>
            ) : null}

            {validationRunModal.controlId || validationRunModal.ruleId || fixPlanItem ? (
              <button
                className="btn btn-ghost btn-xs"
                type="button"
                disabled={fullAssessmentRunning}
                onClick={() => {
                  setValidationRunModal(null);
                  setMode("drift", {
                    framework: validationRunModal.framework || framework,
                    controlId:
                      validationRunModal.controlId ||
                      fixPlanItem?.control_id ||
                      undefined,
                    ruleId:
                      validationRunModal.ruleId ||
                      fixPlanItem?.rule_id ||
                      undefined,
                    resourceId:
                      validationRunModal.resourceId ||
                      fixPlanItem?.resource_id ||
                      undefined,
                  });
                }}
              >
                Open in Drift
              </button>
            ) : null}

            {validationRunModal.tone === "danger" ? (
              <button
                className="btn btn-ghost btn-xs"
                type="button"
                disabled={fullAssessmentRunning}
                onClick={handleRunFullCloudAssessment}
              >
                Run Again
              </button>
            ) : null}

            <button
              className="btn btn-ghost btn-xs"
              type="button"
              disabled={fullAssessmentRunning}
              onClick={() => setValidationRunModal(null)}
            >
              {fullAssessmentRunning ? "Validation Running…" : "Done"}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
